import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { CalendarClock, Check, X, CalendarDays, Clock3, ArrowRight } from 'lucide-react';
import { prettyDate, formatMinutes, lectureMoveLimitReached } from '@/lib/tracker360-moves';

const PREVIEW_COUNT = 3;

/**
 * Compact banner for the immediately previous day's unfinished lectures only.
 * Never renders when there is nothing pending.
 */
export default function YesterdayPendingBanner({
  date,
  lectures,
  onDone,
  onMoveToToday,
  onChooseDate,
  onDismiss,
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  if (!lectures?.length) return null;

  const preview = lectures.slice(0, PREVIEW_COUNT);
  const extra = lectures.length - preview.length;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-amber-500/40 bg-amber-500/5 dark:bg-amber-950/20 p-4 shadow-sm backdrop-blur-xl"
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 rounded-lg bg-amber-500/15 p-1.5 text-amber-600 dark:text-amber-400">
            <CalendarClock className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h4 className="text-sm font-black text-slate-900 dark:text-slate-50">Yesterday's Pending Work</h4>
            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
              Did you complete yesterday's plan? <span className="text-slate-400">• {prettyDate(date)}</span>
            </p>
          </div>
        </div>

        <div className="mt-3 space-y-1.5">
          {preview.map((lec) => (
            <div
              key={lec.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-900/50 px-3 py-2"
            >
              <p className="truncate text-[11px] font-bold text-slate-700 dark:text-slate-200">
                {lec.subject} • {lec.chapterName}
              </p>
              <span className="shrink-0 text-[10px] font-semibold text-slate-400">
                Lec {lec.lectureNumber}
              </span>
            </div>
          ))}
          {extra > 0 && (
            <p className="pl-1 text-[11px] font-semibold text-slate-400">+ {extra} more</p>
          )}
        </div>

        <div className="mt-3.5 flex gap-2">
          <button
            onClick={onDone}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[image:var(--gradient-brand)] py-2.5 text-[11px] font-black text-primary-foreground"
          >
            <Check className="w-3.5 h-3.5" /> Yes, I'm done
          </button>
          <button
            onClick={() => setSheetOpen(true)}
            className="flex-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/50 py-2.5 text-[11px] font-bold text-slate-600 dark:text-slate-300"
          >
            I couldn't finish
          </button>
        </div>
      </motion.div>

      {sheetOpen && typeof document !== 'undefined'
        ? createPortal(
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSheetOpen(false)}
                className="fixed inset-0 z-[115] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm sm:p-4"
              >
                <motion.div
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 40, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 280, damping: 28 }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full sm:max-w-md max-h-[88dvh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-2xl"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-base font-black text-slate-900 dark:text-slate-50">Let's reschedule it</h3>
                      <p className="text-[11px] font-medium text-slate-500">
                        Pending from {prettyDate(date)}
                      </p>
                    </div>
                    <button
                      onClick={() => setSheetOpen(false)}
                      aria-label="Close"
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mt-4 space-y-3">
                    {lectures.map((lec) => {
                      const locked = lectureMoveLimitReached(lec);
                      return (
                        <div
                          key={lec.id}
                          className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 p-3.5"
                        >
                          <p className="truncate text-xs font-bold text-slate-800 dark:text-slate-100">
                            {lec.chapterName}
                          </p>
                          <p className="mt-0.5 flex items-center gap-2 text-[11px] font-medium text-slate-500">
                            {lec.subject} • Lec {lec.lectureNumber}
                            <span className="flex items-center gap-1">
                              <Clock3 className="w-3 h-3" /> {formatMinutes(Number(lec.effectiveDuration) || 0)}
                            </span>
                          </p>
                          {locked ? (
                            <p className="mt-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                              Move limit reached — complete it or adjust your study plan.
                            </p>
                          ) : (
                            <div className="mt-2.5 flex flex-wrap gap-2">
                              <button
                                onClick={() => {
                                  setSheetOpen(false);
                                  onMoveToToday(lec);
                                }}
                                className="flex items-center gap-1.5 rounded-lg bg-[image:var(--gradient-brand)] px-3 py-2 text-[10px] font-black text-primary-foreground"
                              >
                                Move to Today <ArrowRight className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => {
                                  setSheetOpen(false);
                                  onChooseDate(lec);
                                }}
                                className="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-[10px] font-bold text-slate-600 dark:text-slate-300"
                              >
                                <CalendarDays className="w-3 h-3" /> Choose Another Date
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => {
                      setSheetOpen(false);
                      onDismiss();
                    }}
                    className="mt-4 w-full rounded-xl border border-slate-200 dark:border-slate-700 py-2.5 text-[11px] font-bold text-slate-500"
                  >
                    Keep for Later
                  </button>
                </motion.div>
              </motion.div>
            </AnimatePresence>,
            document.body,
          )
        : null}
    </>
  );
}
